import { getNavigationConfig } from '@/lib/navigation/config';
import type { NavigationConfig, NavigationContextType, NavigationState } from '@/types/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

/**
 * Contexte de navigation pour gérer l'état global
 * Suit le pattern des autres contextes du projet
 */

const NavigationContext = createContext<NavigationContextType | null>(null);

interface NavigationProviderProps {
  children: ReactNode;
}

export function NavigationProvider({ children }: NavigationProviderProps) {
  const location = useLocation();
  const { user } = useAuth();

  // Générer la configuration de navigation en fonction du rôle de l'utilisateur
  const config: NavigationConfig = useMemo(() => getNavigationConfig(user), [user]);

  // Trouve l'item actif basé sur la route courante
  const findActiveItemId = useCallback(() => {
    for (const section of config.sections) {
      for (const item of section.items) {
        if (item.href === location.pathname) {
          return item.id;
        }
      }
    }
    return null;
  }, [location.pathname, config.sections]);

  const [state, setState] = useState<NavigationState>({
    activeItemId: findActiveItemId(),
    collapsedSections: [],
    isSearchOpen: false,
  });

  const setActiveItem = useCallback((itemId: string) => {
    setState((prev) => ({ ...prev, activeItemId: itemId }));
  }, []);

  const toggleSection = useCallback((sectionId: string) => {
    setState((prev) => ({
      ...prev,
      collapsedSections: prev.collapsedSections.includes(sectionId)
        ? prev.collapsedSections.filter((id) => id !== sectionId)
        : [...prev.collapsedSections, sectionId],
    }));
  }, []);

  const toggleSearch = useCallback(() => {
    setState((prev) => ({ ...prev, isSearchOpen: !prev.isSearchOpen }));
  }, []);

  // Met à jour l'item actif quand la route change
  useEffect(() => {
    const activeId = findActiveItemId();
    if (activeId !== state.activeItemId) {
      setActiveItem(activeId || '');
    }
  }, [location.pathname, findActiveItemId, state.activeItemId, setActiveItem]);

  const value: NavigationContextType = {
    config,
    state,
    setActiveItem,
    toggleSection,
    toggleSearch,
  };

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}
